import { useEffect, useMemo, useState } from 'react';
import {
  createCanvasMeasurer,
  ptToPx,
  WORD_FONT_FAMILY,
  wordGeometry,
  type TextMeasurer,
  type WordGeometry,
  type WordLang,
} from './wordLineBreaker';

export interface WordMeasurerState {
  geometry: WordGeometry;
  /** Null, bis die Report-Schriften geladen sind (sonst misst die Canvas eine Ersatzschrift). */
  measurer: TextMeasurer | null;
  fontsReady: boolean;
  /** Consolas fehlt lokal → Liberation Mono mit letter-spacing auf 0.5498 em ziehen. */
  codeFallback: boolean;
  /** Zähler, der bei jedem Font-Ladeereignis hochzählt (Neu-Messen). */
  fontEpoch: number;
}

const FONT_LOADS = [
  `400 ${ptToPx(12)}px "STARK Word Narrow"`,
  `700 ${ptToPx(14)}px "STARK Word Narrow"`,
  `italic 400 ${ptToPx(12)}px "STARK Word Narrow"`,
  `400 ${ptToPx(8)}px "STARK Word Code"`,
  `400 ${ptToPx(9)}px "STARK Word Mono"`,
];

/** Ist eine lokale Schrift vorhanden? Vergleich der Canvas-Breite gegen eine Nonsens-Familie. */
export function hasLocalFont(family: string): boolean {
  if (typeof document === 'undefined') return false;
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return false;
  const probe = 'mmmmmmmmmmiiiiiiiiii0000000000';
  ctx.font = `16px "__stark_probe_none__"`;
  const base = ctx.measureText(probe).width;
  ctx.font = `16px "${family}", "__stark_probe_none__"`;
  return Math.abs(ctx.measureText(probe).width - base) > 0.01;
}

export function useWordMeasurer(lang: WordLang = 'de'): WordMeasurerState {
  const geometry = useMemo(() => wordGeometry(lang), [lang]);
  const [fontsReady, setFontsReady] = useState(false);
  const [fontEpoch, setFontEpoch] = useState(0);
  const [codeFallback, setCodeFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
    if (!fonts) {
      setFontsReady(true);
      return undefined;
    }
    Promise.all(FONT_LOADS.map((f) => fonts.load(f).catch(() => [])))
      .then(() => fonts.ready)
      .then(() => {
        if (cancelled) return;
        setCodeFallback(!hasLocalFont('Consolas'));
        setFontsReady(true);
        setFontEpoch((n) => n + 1);
      });
    const onDone = () => setFontEpoch((n) => n + 1);
    fonts.addEventListener?.('loadingdone', onDone);
    return () => {
      cancelled = true;
      fonts.removeEventListener?.('loadingdone', onDone);
    };
  }, []);

  const measurer = useMemo(() => (fontsReady ? createCanvasMeasurer(geometry) : null), [fontsReady, geometry, fontEpoch]);

  return { geometry, measurer, fontsReady, codeFallback, fontEpoch };
}

export const WORD_CSS_FONTS = WORD_FONT_FAMILY;
